import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useReducer,
} from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import axios from "axios";
import auth from "../config/firebase";

const AuthContext = createContext();
const provider = new GoogleAuthProvider();

// Auth state reducer for new declarative parts
const authReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_BACKEND_LOADING":
      return { ...state, backendDataLoading: action.payload };
    case "SET_USER_DATA":
      return {
        ...state,
        currentUser: action.payload.user,
        userObjectId: action.payload.objectId,
        userClaims: action.payload.claims,
        userVerified: action.payload.verified,
      };
    case "CLEAR_USER_DATA":
      return {
        ...state,
        currentUser: null,
        userObjectId: null,
        userClaims: null,
        userVerified: false,
      };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "CLEAR_ERROR":
      return { ...state, error: null };
    default:
      return state;
  }
};

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  // Keep existing useState for backward compatibility
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userClaims, setUserClaims] = useState(null);
  const [error, setError] = useState(null);
  const [userObjectId, setUserObjectId] = useState(null);
  const [userVerified, setUserVerified] = useState(false);
  const [backendDataLoading, setBackendDataLoading] = useState(false);

  // Add reducer for new features (optional migration path)
  const [reducerState, dispatch] = useReducer(authReducer, {
    loading: true,
    backendDataLoading: false,
    currentUser: null,
    userObjectId: null,
    userClaims: null,
    userVerified: false,
    error: null,
  });

  // Keep all existing functions unchanged for backward compatibility
  async function fetchUserObjectId(user) {
    if (!user) return null;

    try {
      const token = await user.getIdToken();
      const payloadHeader = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const requestBody = {
        email: user.email,
        displayName: user.displayName,
      };

      const response = await fetch(
        `/api/diver/check/${user.uid}`,
        {
          method: "POST",
          headers: payloadHeader.headers,
          body: JSON.stringify(requestBody),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.diver && data.exists) {
          return data.diver._id;
        } else {
          return null;
        }
      } else {
        const errorData = await response.json();
        console.error("Backend error checking user ID:", errorData);
        return null;
      }
    } catch (error) {
      console.error("Error fetching user object ID:", error);
      return null;
    }
  }

  async function register(email, password) {
    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      const objectId = await fetchUserObjectId(user);
      if (objectId) {
        setUserObjectId(objectId);
      }
      return userCredential;
    } catch (error) {
      setError(error);
      throw error;
    }
  }

  async function login(email, password) {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;
      const objectId = await fetchUserObjectId(user);
      if (objectId) {
        setUserObjectId(objectId);
      }
      return userCredential;
    } catch (error) {
      setError(error);
      throw error;
    }
  }

  function logout() {
    return signOut(auth);
  }

  function updateUserProfile(user, profile) {
    return updateProfile(user, profile);
  }

  // Enhanced googleAuthHandler with better error handling
  const googleAuthHandler = async (isLoginMode = true) => {
    console.log(`[googleAuthHandler] Called with isLoginMode: ${isLoginMode}`);

    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const token = await user.getIdToken();

      console.log(
        `[googleAuthHandler] Firebase auth successful for: ${user.email}`
      );

      // Check if user exists in backend
      try {
        console.log(`[googleAuthHandler] Checking if user exists: ${user.uid}`);

        const checkResponse = await axios.post(
          `/api/diver/check/${user.uid}`,
          {
            email: user.email,
            displayName: user.displayName,
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const checkData = checkResponse.data;
        console.log(`[googleAuthHandler] Check response:`, checkData);

        if (checkData.exists) {
          console.log("[googleAuthHandler] User exists, logging in");
          return result;
        } else {
          console.log("[googleAuthHandler] User does not exist");

          if (isLoginMode) {
            console.log(
              "[googleAuthHandler] Login mode - redirecting to register"
            );
            await signOut(auth);
            window.location.href = "/register";
            return null;
          } else {
            console.log(
              "[googleAuthHandler] Register mode - creating user in backend"
            );

            try {
              const registerResponse = await axios.post(
                "/api/diver/register-google",
                {
                  uid: user.uid,
                  email: user.email,
                  displayName: user.displayName,
                },
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              console.log(
                "[googleAuthHandler] Registration successful:",
                registerResponse.data
              );
              console.log("New user registered and logged in successfully");
              return result;
            } catch (registerError) {
              console.error(
                "[googleAuthHandler] Registration failed:",
                registerError
              );
              await signOut(auth);
              const errorMessage =
                registerError.response?.data?.message ||
                "Error creating user in backend";
              throw new Error(errorMessage);
            }
          }
        }
      } catch (checkError) {
        console.error("[googleAuthHandler] Check user failed:", checkError);
        await signOut(auth);
        const errorMessage =
          checkError.response?.data?.message ||
          "Unable to verify account. Please try again.";
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error("Google auth error:", error);

      try {
        await signOut(auth);
      } catch (logoutError) {
        console.error("Error during logout:", logoutError);
      }

      setError({ message: error.message, code: error.code });
      throw error;
    }
  };

  // Keep existing useEffect for backward compatibility
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      setLoading(true);

      if (user) {
        console.log("Auth state changed: User logged in", user.email);
        setCurrentUser(user);

        setBackendDataLoading(true);
        try {
          const objectId = await fetchUserObjectId(user);

          if (objectId) {
            setUserObjectId(objectId);
            const idTokenResult = await user.getIdTokenResult(true);
            const claims = {
              isAdmin: idTokenResult.claims.isAdmin === true,
              isBetaUser: idTokenResult.claims.isBetaUser === true,
              isTier1User: idTokenResult.claims.isTier1User === true,
              isTier2User: idTokenResult.claims.isTier2User === true,
              isTier3User: idTokenResult.claims.isTier3User === true,
              isTier4User: idTokenResult.claims.isTier4User === true,
              isTier5User: idTokenResult.claims.isTier5User === true,
            };
            setUserClaims(claims);
            setUserVerified(true);
          } else {
            console.warn(
              "User authenticated by Firebase, but no object ID found from backend. Profile might be incomplete."
            );
            setUserObjectId(null);
            setUserVerified(false);
          }
        } catch (error) {
          console.error("Error fetching user backend data:", error);
          setUserObjectId(null);
          setUserClaims(null);
          setUserVerified(false);
        } finally {
          setBackendDataLoading(false);
        }
      } else {
        console.log("Auth state changed: No user");
        setCurrentUser(null);
        setUserClaims(null);
        setUserObjectId(null);
        setUserVerified(false);
        setBackendDataLoading(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const refreshClaims = async () => {
    if (currentUser) {
      try {
        const idTokenResult = await currentUser.getIdTokenResult(true);
        const claims = {
          isAdmin: idTokenResult.claims.isAdmin === true,
          isBetaUser: idTokenResult.claims.isBetaUser === true,
          isTier1User: idTokenResult.claims.isTier1User === true,
          isTier2User: idTokenResult.claims.isTier2User === true,
          isTier3User: idTokenResult.claims.isTier3User === true,
          isTier4User: idTokenResult.claims.isTier4User === true,
          isTier5User: idTokenResult.claims.isTier5User === true,
        };
        setUserClaims(claims);
      } catch (error) {
        console.error("Error refreshing claims:", error);
      }
    }
  };

  // Helper functions for new declarative patterns (optional)
  const declarativeHelpers = {
    // Pure state access
    getAuthState: () => ({
      currentUser,
      userObjectId,
      userClaims,
      userVerified,
      loading,
      backendDataLoading,
      error,
    }),

    // Action creators for components that want to use reducer pattern
    createAction: (type, payload) => ({ type, payload }),

    // Async action wrapper
    withAsyncAction: async (actionFn, successAction, errorAction) => {
      try {
        const result = await actionFn();
        if (successAction) dispatch(successAction(result));
        return result;
      } catch (error) {
        if (errorAction) dispatch(errorAction(error));
        throw error;
      }
    },
  };

  // Context value - 100% backward compatible
  const value = {
    // Existing API (unchanged)
    currentUser,
    userClaims,
    refreshClaims,
    error,
    setError,
    login,
    register,
    logout,
    updateUserProfile,
    googleAuthHandler,
    userObjectId,
    loading,
    backendDataLoading,
    userVerified,

    // Optional new declarative helpers for gradual migration
    ...declarativeHelpers,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
